/**
 * useSavedView — persist column preferences (order, width, sort, density)
 * per user AND per company. Debounces writes to avoid a request storm.
 *
 * Data resolution priority (per-field):
 *   personal (user override) > company default > built-in fallback
 *
 * Usage:
 *   const view = useSavedView("job_applicants", {
 *       columns: [{id:"name"}, {id:"score", width:170}, ...],
 *       sort:    { by:"score", dir:"desc" },
 *       density: "comfortable",
 *   });
 *   view.value.columns       // resolved list
 *   view.value.sort          // resolved sort
 *   view.update({ columns })  // patch + auto-save (debounced 700ms)
 *   view.saveAsCompanyDefault() // owner/admin action
 *   view.resetPersonal()     // wipe user override, fall back to company default
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEBOUNCE_MS = 700;

function merge(fallback, companyDefault, personal) {
    // Field-level merge: personal > company > fallback.
    return {
        columns: personal?.columns || companyDefault?.columns || fallback?.columns || [],
        sort: personal?.sort || companyDefault?.sort || fallback?.sort || null,
        filters: personal?.filters || companyDefault?.filters || fallback?.filters || null,
        density: personal?.density || companyDefault?.density || fallback?.density || null,
    };
}

export function useSavedView(viewKey, fallback = {}) {
    const [personal, setPersonal] = useState(null);
    const [companyDefault, setCompanyDefault] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const saveTimerRef = useRef(null);
    const skipNextSaveRef = useRef(true);   // don't save the initial load

    // Initial fetch — one shot on mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/views/${viewKey}`);
                if (cancelled) return;
                setPersonal(data.personal || null);
                setCompanyDefault(data.company_default || null);
            } catch {
                // View endpoint failure is non-fatal — the table renders with its fallback.
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [viewKey]);

    const value = useMemo(
        () => merge(fallback, companyDefault, personal),
        [fallback, companyDefault, personal],
    );

    const scheduleSave = useCallback((next) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                await api.put(`/views/${viewKey}`, next);
            } catch {
                // Never surface — this is a background persist.
            }
        }, DEBOUNCE_MS);
    }, [viewKey]);

    /** Patch the personal view — fields you don't include are preserved. */
    const update = useCallback((patch) => {
        setPersonal((cur) => {
            const next = { ...(cur || {}), ...patch };
            scheduleSave(next);
            return next;
        });
    }, [scheduleSave]);

    /** Promote the current personal view (or the resolved value if no personal
     *  override exists yet) to the company-wide default. */
    const saveAsCompanyDefault = useCallback(async () => {
        const payload = {
            columns: value.columns,
            sort: value.sort,
            filters: value.filters,
            density: value.density,
        };
        try {
            const { data } = await api.put(`/views/${viewKey}/company`, payload);
            setCompanyDefault(data?.saved || payload);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: e };
        }
    }, [value, viewKey]);

    /** Delete this user's personal override — the resolved view will now
     *  match the company default (or the built-in fallback). */
    const resetPersonal = useCallback(async () => {
        try {
            await api.delete(`/views/${viewKey}`);
            setPersonal(null);
            skipNextSaveRef.current = true;
        } catch {
            /* noop */
        }
    }, [viewKey]);

    return { value, loaded, update, saveAsCompanyDefault, resetPersonal };
}
